import encodeQR from 'qr';
import { useMemo } from 'react';

export interface QrCodeProps {
  value: string;
  /** Accessible name; the image carries no other text. */
  label: string;
  size?: number;
}

/** One path of 1×1 squares, one per dark module. */
function modulesPath(modules: boolean[][]): string {
  let d = '';
  modules.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) d += `M${x} ${y}h1v1h-1z`;
    });
  });
  return d;
}

/**
 * QR code drawn as inline SVG from the `qr` encoder's module matrix. Black on white in both
 * themes on purpose: authenticator apps scan inverted codes unreliably, so the theme tokens are
 * not used here.
 */
export function QrCode({ value, label, size = 184 }: QrCodeProps) {
  const { path, dimension } = useMemo(() => {
    const modules = encodeQR(value, 'raw', { ecc: 'medium', border: 2 });
    return { path: modulesPath(modules), dimension: modules.length };
  }, [value]);

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${dimension} ${dimension}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      className="shrink-0 rounded-md border"
      data-testid="two-factor-qr"
    >
      <rect width={dimension} height={dimension} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  );
}
